import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useActiveLocationId } from '@/lib/locationContext';
import { viewRpcArgs } from '@/lib/viewAs';
import { isCell } from '@/lib/cell/config';
import { cellGet, viewQuery } from '@/lib/cell/api';

/**
 * THE PRO HUB FEATURE SWITCHBOARD, client half (Peter, 2026-08-27: "maximum
 * flexibility so the foundation has to be correct" — pilot one pro, one
 * studio, or everywhere).
 *
 * The precedence (pro override > location grant > default OFF) lives in ONE
 * place: public.pro_hub_feature_enabled, called here by rpc. This hook never
 * re-derives it — two derivations is how two surfaces come to disagree.
 *
 * DEFAULT OFF, the OPPOSITE of the referral contract (absent-means-on,
 * LIB-13): referral is a standing programme a studio opts out of; a feature
 * here is a PILOT a studio opts into. While loading, the feature stays HIDDEN
 * for the same reason the Refer gate does: a door that appears is fine, one
 * that vanishes after a beat reads as the app taking something away.
 */
export function useFeature(featureKey: string): { loading: boolean; enabled: boolean } {
  const { activeLocationId } = useActiveLocationId();
  const { data, isLoading } = useQuery({
    queryKey: ['pro-feature', featureKey, activeLocationId],
    enabled: !!activeLocationId,
    staleTime: 60_000,
    queryFn: async () => {
      // CELL (M28): GET /v1/pro-hub/locations/{locationId}/features/{featureKey} — the same function decides.
      const { data: on, error } = isCell()
        ? await cellGet<{ enabled: boolean }>(`/v1/pro-hub/locations/${activeLocationId}/features/${featureKey}`, viewQuery()).then((r) => ({ data: r.data?.enabled ?? null, error: r.error }))
        : await supabase.rpc('pro_hub_feature_enabled', {
        p_feature: featureKey,
        p_location: activeLocationId!,
        ...viewRpcArgs(), // view-as sees the feature as THE PRO does (per-pro overrides included)
      });
      // Fail CLOSED: an unreadable switchboard is a dark feature, never an
      // open fence — and say why in the console rather than a bare swallow.
      if (error) {
        console.error('feature switchboard read failed', featureKey, error.message);
        return false;
      }
      return on === true;
    },
  });
  return { loading: isLoading, enabled: data === true };
}
